'use client';

import { useState } from 'react';

import { RatingsSummary } from './RatingsSummary';

import get from 'ts-get';
import { useRutilusQuery } from '../../../../api/graphql/useRutilusQuery';
import { ReviewCard } from '../../../../components/Cards/ReviewCard/ReviewCard';
import { Button } from '../../../../components/Clickables/Buttons/Button';
import { Container } from '../../../../components/Container/Container';
import { Heading } from '../../../../components/Heading/Heading';
import { LoadingSpinner } from '../../../../components/LoadingSpinner/LoadingSpinner';
import { Select } from '../../../../components/Select/Select';
import { Text } from '../../../../components/Text/Text';
import { isBrowser } from '../../../../helpers/environment/isBrowser';
import { reportMykissClientError } from '../../../../helpers/errorHandling/client';
import { getNodesFromConnection } from '../../../../helpers/graphql';
import { useClientHasRendered } from '../../../../helpers/hooks';
import { useIntersectionObserver } from '../../../../helpers/hooks/useIntersectionObserver';
import {
  GetFishingWater_fishingWater_allReviews,
  GetFishingWaterReviews,
  GetFishingWaterReviews_fishingWater_reviews_edges_node,
  GetFishingWaterReviewsVariables,
} from '../../../../interfaces/graphql';
import { getFishingWaterReviews } from '../../../../queries/rutilus/FishingWaters';
import { SUB_LINK_IDS } from '../../helpers/fishingWaterHelpers';
import { NoReviewsCTASection } from './NoReviewsCTASection';

interface IProps {
  allReviews: GetFishingWater_fishingWater_allReviews | null;
  fishingWaterName: string;
  fishingWaterId: string;
}

interface SortOption {
  id: string;
  label: string;
  value: 'top-reviews' | 'most-recent';
}

const SORT_OPTIONS: SortOption[] = [
  { id: 'top-reviews', label: 'Top reviews', value: 'top-reviews' },
  { id: 'most-recent', label: 'Most recent', value: 'most-recent' },
];

const REVIEWS_PER_REQUEST = 6;

export const ReviewsSection = ({ allReviews, fishingWaterName, fishingWaterId }: IProps) => {
  const clientHasRendered = useClientHasRendered();
  const [isInView, ref] = useIntersectionObserver({ rootMargin: '500px' });

  const [sortBy, setSortBy] = useState<SortOption['value']>('top-reviews');
  const variables = {
    first: REVIEWS_PER_REQUEST,
    externalId: fishingWaterId,
    orderByTopReviews: sortBy === 'top-reviews',
    orderByRecent: sortBy === 'most-recent',
  };

  const { data, loading, fetchMore } = useRutilusQuery<
    GetFishingWaterReviews,
    GetFishingWaterReviewsVariables
  >(getFishingWaterReviews, {
    variables,
    skip: !isBrowser() || !isInView,
  });

  const hasNextPage = data?.fishingWater.reviews?.pageInfo.hasNextPage ?? false;
  const endCursor = data?.fishingWater.reviews?.pageInfo.endCursor;

  const fetchNextPage = async (): Promise<unknown> =>
    fetchMore({
      variables: {
        ...variables,
        after: endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }): GetFishingWaterReviews => {
        if (!fetchMoreResult.fishingWater.reviews) {
          return prev;
        }
        return {
          fishingWater: {
            ...fetchMoreResult.fishingWater,
            reviews: {
              ...fetchMoreResult.fishingWater.reviews,
              edges: [
                ...get(prev, p => p.fishingWater.reviews.edges, []),
                ...get(fetchMoreResult, r => r.fishingWater.reviews.edges, []),
              ],
            },
          },
        };
      },
    }).catch(reportMykissClientError);

  const reviewList = getNodesFromConnection<GetFishingWaterReviews_fishingWater_reviews_edges_node>(
    data?.fishingWater.reviews,
  );

  const hasWrittenReviews = reviewList.length > 0;

  return (
    <Container
      id={SUB_LINK_IDS.REVIEWS}
      className="mb-fw grid scroll-mt-(--navigation-scrolltop-offset-mobile) grid-cols-4 gap-3 sm:scroll-mt-(--navigation-scrolltop-offset) md:grid-cols-12 md:gap-4"
    >
      <div
        ref={ref}
        className="order-1 col-span-full mb-3 flex flex-col justify-between sm:flex-row sm:items-center md:col-span-6 md:col-start-1 md:mb-0 lg:col-start-2"
      >
        <Heading className="w-col-4 sm:w-col-9 md:w-col-7 mb-2 sm:mb-0" variant="md">
          {`Reviews of ${fishingWaterName}`}
        </Heading>
      </div>

      <div className="order-2 col-span-full md:order-3 md:col-span-4 md:col-start-7 md:px-6 lg:col-start-8">
        <RatingsSummary
          averageRating={allReviews?.totalAverage ?? 0}
          reviewCount={allReviews?.totalCount ?? 0}
          hasWrittenReviews={hasWrittenReviews}
          oneStarReviews={data?.fishingWater.oneStarReviews?.totalCount}
          twoStarReviews={data?.fishingWater.twoStarReviews?.totalCount}
          threeStarReviews={data?.fishingWater.threeStarReviews?.totalCount}
          fourStarReviews={data?.fishingWater.fourStarReviews?.totalCount}
          fiveStarReviews={data?.fishingWater.fiveStarReviews?.totalCount}
          sourceId={fishingWaterId}
        />
      </div>

      <div className="order-3 col-span-full md:order-2 md:col-span-6 md:col-start-1 lg:col-start-2">
        {clientHasRendered && !loading && reviewList.length === 0 ? (
          <div className="mt-3 md:mt-0">
            <NoReviewsCTASection />
          </div>
        ) : (
          <>
            <div className="mt-3 flex w-full flex-row items-center justify-between">
              {data?.fishingWater.reviews?.totalCount && (
                <Text
                  variant="sm"
                  className="m-0"
                >{`${data.fishingWater.reviews.totalCount} ${data.fishingWater.reviews.totalCount === 1 ? ' review' : ' reviews'}`}</Text>
              )}

              <Select
                id="sort-options"
                isDisabled={loading}
                onChange={value => {
                  const { value: sortBy } = value as SortOption;
                  setSortBy(sortBy);
                }}
                options={SORT_OPTIONS}
                defaultValue={SORT_OPTIONS.find(({ value }) => value === sortBy)!}
                aria-label="Sort reviews"
              />
            </div>
            <div className={`relative flex ${loading ? 'min-h-48' : ''} flex-col`}>
              {loading && (
                <LoadingSpinner className="z-high absolute inset-0 m-0 flex size-full items-center justify-center! bg-white/25 p-0" />
              )}
              {reviewList.map(review => (
                <ReviewCard
                  key={`water-reviews-${review.externalId}`}
                  rating={review.rating}
                  heading={review.post.titleString}
                  bodyText={review.post.text.text}
                  username={review.author.nickname}
                  creationDate={review.createdAt}
                  className="w-full"
                  showAllText
                />
              ))}
            </div>

            {hasNextPage && (
              <Button
                size="sm"
                className="mt-2 w-full p-2"
                variant="textPrimary"
                onClick={fetchNextPage}
              >
                Show more reviews
              </Button>
            )}
          </>
        )}
      </div>
    </Container>
  );
};
